/**
 * Transform Node Specifications
 *
 * Direct port from Vue frontend — framework-agnostic TypeScript.
 * Defines all node types available in the Transform visual builder.
 */

export interface FieldOption {
  value: string;
  label: string;
}

export interface FieldSpec {
  name: string;
  label: string;
  type:
    | 'text'
    | 'select'
    | 'multiselect'
    | 'columns'
    | 'expression'
    | 'number'
    | 'boolean'
    | 'filter'
    | 'aggregation'
    | 'variable'
    | 'joinConditions';
  required?: boolean;
  placeholder?: string;
  options?: FieldOption[];
  default?: unknown;
  showWhen?: { field: string; value: unknown };
}

export interface FilterCondition {
  column: string;
  operator: string;
  value: string;
  valueType: 'string' | 'number' | 'column' | 'boolean' | 'null';
}

export interface JoinConditionPair {
  leftColumn: string;
  rightColumn: string;
}

export interface AggregationSpec {
  column: string;
  function: string;
  alias: string;
}

export const FILTER_OPERATORS = {
  string: [
    { value: '==', label: 'equals' },
    { value: '!=', label: 'not equals' },
    { value: 'contains', label: 'contains' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'ends_with', label: 'ends with' },
    { value: 'is_null', label: 'is null' },
    { value: 'is_not_null', label: 'is not null' },
    { value: 'in', label: 'in list' },
  ],
  number: [
    { value: '==', label: 'equals' },
    { value: '!=', label: 'not equals' },
    { value: '>', label: 'greater than' },
    { value: '>=', label: 'greater or equal' },
    { value: '<', label: 'less than' },
    { value: '<=', label: 'less or equal' },
    { value: 'between', label: 'between' },
    { value: 'is_null', label: 'is null' },
    { value: 'is_not_null', label: 'is not null' },
  ],
  boolean: [
    { value: '==', label: 'equals' },
    { value: 'is_null', label: 'is null' },
    { value: 'is_not_null', label: 'is not null' },
  ],
  date: [
    { value: '==', label: 'equals' },
    { value: '!=', label: 'not equals' },
    { value: '>', label: 'after' },
    { value: '>=', label: 'on or after' },
    { value: '<', label: 'before' },
    { value: '<=', label: 'on or before' },
    { value: 'between', label: 'between' },
    { value: 'is_null', label: 'is null' },
    { value: 'is_not_null', label: 'is not null' },
  ],
};

export const AGGREGATION_FUNCTIONS = [
  { value: 'count', label: 'Count' },
  { value: 'count_distinct', label: 'Count Distinct' },
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
  { value: 'first', label: 'First' },
  { value: 'last', label: 'Last' },
];

export interface NodeSpec {
  type: string;
  label: string;
  description: string;
  icon: string;
  category: 'entry' | 'transform' | 'exit';
  color: string;
  inputs: number;
  outputs: number;
  inputLabels?: string[];
  fields: FieldSpec[];
  generateDsl: (config: Record<string, unknown>, ...inputVars: string[]) => string;
}

// ============================================================================
// ENTRY NODES
// ============================================================================

export const SOURCE_NODE: NodeSpec = {
  type: 'source',
  label: 'Source',
  description: 'Select a table from the catalog',
  icon: 'Database',
  category: 'entry',
  color: '#3b82f6',
  inputs: 0,
  outputs: 1,
  fields: [
    { name: 'table', label: 'Table', type: 'select', required: true, placeholder: 'Select a table...' },
    { name: 'variable', label: 'Variable Name', type: 'text', required: true, placeholder: 'users' },
  ],
  generateDsl: (config) => `${String(config.variable)} = source("${String(config.table)}")`,
};

// ============================================================================
// TRANSFORM NODES
// ============================================================================

export const FILTER_NODE: NodeSpec = {
  type: 'filter',
  label: 'Filter',
  description: 'Filter rows based on a condition',
  icon: 'Filter',
  category: 'transform',
  color: '#8b5cf6',
  inputs: 1,
  outputs: 1,
  fields: [{ name: 'filters', label: 'Filter Conditions', type: 'filter', required: true }],
  generateDsl: (config, inputVar) => {
    const filters = (config.filters || []) as FilterCondition[];
    if (filters.length === 0) return inputVar;

    const conditions = filters.map((f) => {
      const col = f.column;
      const op = f.operator;
      const val = f.value;
      const valType = f.valueType || 'string';

      if (op === 'is_null') return `${col} == null`;
      if (op === 'is_not_null') return `${col} != null`;

      let formattedVal: string = val;
      if (valType === 'string') {
        formattedVal = `"${val}"`;
      }

      switch (op) {
        case '==':
          return `${col} == ${formattedVal}`;
        case '!=':
          return `${col} != ${formattedVal}`;
        case '>':
          return `${col} > ${formattedVal}`;
        case '>=':
          return `${col} >= ${formattedVal}`;
        case '<':
          return `${col} < ${formattedVal}`;
        case '<=':
          return `${col} <= ${formattedVal}`;
        case 'contains':
          return `${col}.contains(${formattedVal})`;
        case 'starts_with':
          return `${col}.starts_with(${formattedVal})`;
        case 'ends_with':
          return `${col}.ends_with(${formattedVal})`;
        case 'in':
          return `${col}.is_in([${val}])`;
        case 'between': {
          const [low, high] = val.split(',').map((v: string) => v.trim());
          return `${col} >= ${low} && ${col} <= ${high}`;
        }
        default:
          return `${col} ${op} ${formattedVal}`;
      }
    });

    return `${inputVar}.filter(${conditions.join(' && ')})`;
  },
};

export const SELECT_NODE: NodeSpec = {
  type: 'select',
  label: 'Select Columns',
  description: 'Select specific columns to keep',
  icon: 'Columns3',
  category: 'transform',
  color: '#22c55e',
  inputs: 1,
  outputs: 1,
  fields: [{ name: 'columns', label: 'Columns', type: 'columns', required: true }],
  generateDsl: (config, inputVar) => `${inputVar}.select([${(config.columns as string[])?.join(', ') || ''}])`,
};

export const DROP_NODE: NodeSpec = {
  type: 'drop',
  label: 'Drop Columns',
  description: 'Remove specific columns',
  icon: 'Trash2',
  category: 'transform',
  color: '#ef4444',
  inputs: 1,
  outputs: 1,
  fields: [{ name: 'columns', label: 'Columns to Drop', type: 'columns', required: true }],
  generateDsl: (config, inputVar) => `${inputVar}.drop([${(config.columns as string[])?.join(', ') || ''}])`,
};

export const DEDUPE_NODE: NodeSpec = {
  type: 'dedupe',
  label: 'Deduplicate',
  description: 'Remove duplicate rows',
  icon: 'CopyMinus',
  category: 'transform',
  color: '#f97316',
  inputs: 1,
  outputs: 1,
  fields: [
    { name: 'columns', label: 'Key Columns', type: 'columns', required: true },
    {
      name: 'keep',
      label: 'Keep',
      type: 'select',
      options: [
        { value: 'first', label: 'First' },
        { value: 'last', label: 'Last' },
        { value: 'any', label: 'Any' },
      ],
      default: 'last',
    },
  ],
  generateDsl: (config, inputVar) =>
    `${inputVar}.dedupe([${(config.columns as string[])?.join(', ') || ''}], keep: ${String((config.keep as string | undefined) || 'last')})`,
};

export const SORT_NODE: NodeSpec = {
  type: 'sort',
  label: 'Sort',
  description: 'Order rows by columns',
  icon: 'ArrowUpDown',
  category: 'transform',
  color: '#06b6d4',
  inputs: 1,
  outputs: 1,
  fields: [
    { name: 'columns', label: 'Sort By', type: 'columns', required: true },
    {
      name: 'order',
      label: 'Order',
      type: 'select',
      options: [
        { value: 'asc', label: 'Ascending' },
        { value: 'desc', label: 'Descending' },
      ],
      default: 'asc',
    },
  ],
  generateDsl: (config, inputVar) => {
    const cols =
      (config.columns as string[])
        ?.map((c) => `${c}: ${String((config.order as string | undefined) || 'asc')}`)
        .join(', ') || '';
    return `${inputVar}.sort([${cols}])`;
  },
};

export const LIMIT_NODE: NodeSpec = {
  type: 'limit',
  label: 'Limit',
  description: 'Limit number of rows',
  icon: 'Hash',
  category: 'transform',
  color: '#64748b',
  inputs: 1,
  outputs: 1,
  fields: [{ name: 'count', label: 'Row Count', type: 'number', required: true, default: 100, placeholder: '100' }],
  generateDsl: (config, inputVar) => `${inputVar}.limit(${String((config.count as number | undefined) || 100)})`,
};

export const RENAME_NODE: NodeSpec = {
  type: 'rename',
  label: 'Rename',
  description: 'Rename columns',
  icon: 'Pencil',
  category: 'transform',
  color: '#ec4899',
  inputs: 1,
  outputs: 1,
  fields: [
    { name: 'oldName', label: 'Current Name', type: 'columns', required: true },
    { name: 'newName', label: 'New Name', type: 'text', required: true, placeholder: 'new_column_name' },
  ],
  generateDsl: (config, inputVar) => `${inputVar}.rename(${String(config.oldName)}: "${String(config.newName)}")`,
};

export const CAST_NODE: NodeSpec = {
  type: 'cast',
  label: 'Cast Type',
  description: 'Change column data type',
  icon: 'RefreshCw',
  category: 'transform',
  color: '#a855f7',
  inputs: 1,
  outputs: 1,
  fields: [
    { name: 'column', label: 'Column', type: 'columns', required: true },
    {
      name: 'toType',
      label: 'Target Type',
      type: 'select',
      required: true,
      options: [
        { value: 'string', label: 'String' },
        { value: 'int', label: 'Integer' },
        { value: 'long', label: 'Long' },
        { value: 'float', label: 'Float' },
        { value: 'double', label: 'Double' },
        { value: 'boolean', label: 'Boolean' },
        { value: 'date', label: 'Date' },
        { value: 'timestamp', label: 'Timestamp' },
      ],
    },
  ],
  generateDsl: (config, inputVar) => `${inputVar}.cast(${String(config.column)}: "${String(config.toType)}")`,
};

export const ADD_NODE: NodeSpec = {
  type: 'add',
  label: 'Add Column',
  description: 'Add a computed column',
  icon: 'PlusSquare',
  category: 'transform',
  color: '#14b8a6',
  inputs: 1,
  outputs: 1,
  fields: [
    { name: 'name', label: 'Column Name', type: 'text', required: true, placeholder: 'new_column' },
    { name: 'expression', label: 'Expression', type: 'expression', required: true, placeholder: 'col1 + col2' },
  ],
  generateDsl: (config, inputVar) => `${inputVar}.add(${String(config.name)}: ${String(config.expression)})`,
};

export const WITH_COLUMNS_NODE: NodeSpec = {
  type: 'with_columns',
  label: 'With Columns',
  description: 'Add multiple computed columns',
  icon: 'Layers',
  category: 'transform',
  color: '#0ea5e9',
  inputs: 1,
  outputs: 1,
  fields: [
    {
      name: 'expressions',
      label: 'Column Expressions',
      type: 'expression',
      required: true,
      placeholder: 'col1: expr1, col2: expr2',
    },
  ],
  generateDsl: (config, inputVar) => `${inputVar}.with_columns(${String(config.expressions)})`,
};

export const JOIN_NODE: NodeSpec = {
  type: 'join',
  label: 'Join',
  description: 'Join two tables with paired column conditions',
  icon: 'Merge',
  category: 'transform',
  color: '#6366f1',
  inputs: 2,
  outputs: 1,
  inputLabels: ['Left', 'Right'],
  fields: [
    {
      name: 'joinType',
      label: 'Join Type',
      type: 'select',
      options: [
        { value: 'inner', label: 'Inner' },
        { value: 'left', label: 'Left' },
        { value: 'right', label: 'Right' },
        { value: 'full', label: 'Full Outer' },
      ],
      default: 'left',
    },
    { name: 'joinConditions', label: 'Join Conditions', type: 'joinConditions', required: true },
  ],
  generateDsl: (config, leftVar, rightVar) => {
    const joinType = String((config.joinType as string | undefined) || 'left');
    const left = leftVar || 'left';
    const right = rightVar || 'right';

    if ((config.onColumns as string[])?.length && !(config.joinConditions as JoinConditionPair[])?.length) {
      return `${left}.join(${right}, on: [${(config.onColumns as string[]).join(', ')}], type: "${joinType}")`;
    }

    const conditions: JoinConditionPair[] = (config.joinConditions as JoinConditionPair[]) || [];
    if (conditions.length === 0) {
      return `${left}.join(${right}, type: "${joinType}")`;
    }

    const allSame = conditions.every((c) => c.leftColumn === c.rightColumn);

    if (allSame) {
      const cols = conditions.map((c) => c.leftColumn);
      return `${left}.join(${right}, on: [${cols.join(', ')}], type: "${joinType}")`;
    } else {
      const leftCols = conditions.map((c) => c.leftColumn);
      const rightCols = conditions.map((c) => c.rightColumn);
      return `${left}.join(${right}, left_on: [${leftCols.join(', ')}], right_on: [${rightCols.join(', ')}], type: "${joinType}")`;
    }
  },
};

export const GROUP_BY_NODE: NodeSpec = {
  type: 'group_by',
  label: 'Group By',
  description: 'Group rows and aggregate',
  icon: 'Group',
  category: 'transform',
  color: '#f59e0b',
  inputs: 1,
  outputs: 1,
  fields: [
    { name: 'columns', label: 'Group By Columns', type: 'columns', required: true },
    { name: 'aggregations', label: 'Aggregations', type: 'aggregation', required: true },
  ],
  generateDsl: (config, inputVar) => {
    const groupCols = (config.columns as string[])?.join(', ') || '';
    const aggs = (config.aggregations || []) as AggregationSpec[];

    const aggParts = aggs.map((agg) => {
      if (agg.function === 'count' && agg.column === '*') {
        return `count() as ${agg.alias}`;
      } else if (agg.function === 'count_distinct') {
        return `count_distinct(${agg.column}) as ${agg.alias}`;
      } else {
        return `${agg.function}(${agg.column}) as ${agg.alias}`;
      }
    });

    return `${inputVar}.group_by([${groupCols}]).agg([${aggParts.join(', ')}])`;
  },
};

export const WINDOW_NODE: NodeSpec = {
  type: 'window',
  label: 'Window',
  description: 'Window function computation',
  icon: 'LayoutGrid',
  category: 'transform',
  color: '#84cc16',
  inputs: 1,
  outputs: 1,
  fields: [
    { name: 'partitionBy', label: 'Partition By', type: 'columns', required: false },
    { name: 'orderBy', label: 'Order By', type: 'columns', required: false },
    {
      name: 'expression',
      label: 'Window Expression',
      type: 'expression',
      required: true,
      placeholder: 'row_number() as rn',
    },
  ],
  generateDsl: (config, inputVar) => {
    let dsl = `${inputVar}.window(`;
    if ((config.partitionBy as string[])?.length) {
      dsl += `partition: [${(config.partitionBy as string[]).join(', ')}], `;
    }
    if ((config.orderBy as string[])?.length) {
      dsl += `order: [${(config.orderBy as string[]).join(', ')}], `;
    }
    dsl += `expr: ${String(config.expression)})`;
    return dsl;
  },
};

export const PIVOT_NODE: NodeSpec = {
  type: 'pivot',
  label: 'Pivot',
  description: 'Pivot rows to columns',
  icon: 'RotateCw',
  category: 'transform',
  color: '#d946ef',
  inputs: 1,
  outputs: 1,
  fields: [
    { name: 'index', label: 'Index Columns', type: 'columns', required: true },
    { name: 'columns', label: 'Pivot Column', type: 'columns', required: true },
    { name: 'values', label: 'Value Columns', type: 'columns', required: true },
    {
      name: 'aggFunc',
      label: 'Aggregate Function',
      type: 'select',
      options: [
        { value: 'sum', label: 'Sum' },
        { value: 'count', label: 'Count' },
        { value: 'avg', label: 'Average' },
        { value: 'min', label: 'Min' },
        { value: 'max', label: 'Max' },
      ],
      default: 'sum',
    },
  ],
  generateDsl: (config, inputVar) =>
    `${inputVar}.pivot(index: [${(config.index as string[])?.join(', ')}], columns: ${(config.columns as string[])?.[0]}, values: [${(config.values as string[])?.join(', ')}], agg: "${String((config.aggFunc as string | undefined) || 'sum')}")`,
};

export const UNPIVOT_NODE: NodeSpec = {
  type: 'unpivot',
  label: 'Unpivot',
  description: 'Unpivot columns to rows',
  icon: 'RotateCcw',
  category: 'transform',
  color: '#c026d3',
  inputs: 1,
  outputs: 1,
  fields: [
    { name: 'idColumns', label: 'ID Columns', type: 'columns', required: true },
    { name: 'valueColumns', label: 'Value Columns', type: 'columns', required: true },
    { name: 'nameColumn', label: 'Name Column', type: 'text', default: 'variable', placeholder: 'variable' },
    { name: 'valueColumn', label: 'Value Column', type: 'text', default: 'value', placeholder: 'value' },
  ],
  generateDsl: (config, inputVar) =>
    `${inputVar}.unpivot(id: [${(config.idColumns as string[])?.join(', ')}], values: [${(config.valueColumns as string[])?.join(', ')}], name: "${String((config.nameColumn as string | undefined) || 'variable')}", value: "${String((config.valueColumn as string | undefined) || 'value')}")`,
};

export const VALIDATE_NODE: NodeSpec = {
  type: 'validate',
  label: 'Validate',
  description: 'Data quality validation',
  icon: 'CheckCircle',
  category: 'transform',
  color: '#10b981',
  inputs: 1,
  outputs: 1,
  fields: [
    {
      name: 'rules',
      label: 'Validation Rules',
      type: 'expression',
      required: true,
      placeholder: 'not_null(id), unique(email)',
    },
    {
      name: 'onFail',
      label: 'On Failure',
      type: 'select',
      options: [
        { value: 'filter', label: 'Filter Invalid Rows' },
        { value: 'tag', label: 'Tag Invalid Rows' },
        { value: 'fail', label: 'Fail Pipeline' },
      ],
      default: 'filter',
    },
  ],
  generateDsl: (config, inputVar) =>
    `${inputVar}.validate([${String(config.rules)}], on_fail: "${String((config.onFail as string | undefined) || 'filter')}")`,
};

export const SCD2_NODE: NodeSpec = {
  type: 'scd2',
  label: 'SCD Type 2',
  description: 'Slowly changing dimension type 2',
  icon: 'History',
  category: 'transform',
  color: '#7c3aed',
  inputs: 1,
  outputs: 1,
  fields: [
    { name: 'keyColumns', label: 'Business Key Columns', type: 'columns', required: true },
    { name: 'trackColumns', label: 'Track Columns', type: 'columns', required: true },
    {
      name: 'startDateCol',
      label: 'Start Date Column',
      type: 'text',
      default: 'valid_from',
      placeholder: 'valid_from',
    },
    { name: 'endDateCol', label: 'End Date Column', type: 'text', default: 'valid_to', placeholder: 'valid_to' },
    { name: 'currentCol', label: 'Is Current Column', type: 'text', default: 'is_current', placeholder: 'is_current' },
  ],
  generateDsl: (config, inputVar) =>
    `${inputVar}.scd2(keys: [${(config.keyColumns as string[])?.join(', ')}], track: [${(config.trackColumns as string[])?.join(', ')}], start_date: "${String((config.startDateCol as string | undefined) || 'valid_from')}", end_date: "${String((config.endDateCol as string | undefined) || 'valid_to')}", is_current: "${String((config.currentCol as string | undefined) || 'is_current')}")`,
};

export const CDC_NODE: NodeSpec = {
  type: 'cdc',
  label: 'CDC',
  description: 'Change data capture',
  icon: 'GitCompare',
  category: 'transform',
  color: '#be185d',
  inputs: 2,
  outputs: 1,
  fields: [
    { name: 'keyColumns', label: 'Key Columns', type: 'columns', required: true },
    {
      name: 'mode',
      label: 'CDC Mode',
      type: 'select',
      options: [
        { value: 'full', label: 'Full (Insert/Update/Delete)' },
        { value: 'upsert', label: 'Upsert Only' },
      ],
      default: 'full',
    },
  ],
  generateDsl: (config, currentVar, previousVar) =>
    `${currentVar}.cdc(${previousVar}, keys: [${(config.keyColumns as string[])?.join(', ')}], mode: "${String((config.mode as string | undefined) || 'full')}")`,
};

export const CHART_NODE: NodeSpec = {
  type: 'chart',
  label: 'Chart',
  description: 'Create a visualization',
  icon: 'BarChart2',
  category: 'transform',
  color: '#0ea5e9',
  inputs: 1,
  outputs: 1,
  fields: [
    {
      name: 'chartType',
      label: 'Chart Type',
      type: 'select',
      required: true,
      options: [
        { value: 'bar', label: 'Bar Chart' },
        { value: 'line', label: 'Line Chart' },
        { value: 'area', label: 'Area Chart' },
        { value: 'pie', label: 'Pie Chart' },
        { value: 'scatter', label: 'Scatter Plot' },
        { value: 'histogram', label: 'Histogram' },
      ],
      default: 'bar',
    },
    { name: 'xColumn', label: 'X Axis', type: 'columns', required: true },
    { name: 'yColumn', label: 'Y Axis', type: 'columns', required: true },
    {
      name: 'yAggregation',
      label: 'Y Aggregation',
      type: 'select',
      options: [
        { value: '', label: 'None (use value)' },
        { value: 'count', label: 'Count' },
        { value: 'sum', label: 'Sum' },
        { value: 'avg', label: 'Average' },
        { value: 'min', label: 'Min' },
        { value: 'max', label: 'Max' },
      ],
      default: '',
    },
    { name: 'colorColumn', label: 'Color By (optional)', type: 'columns' },
    { name: 'title', label: 'Chart Title', type: 'text', placeholder: 'My Chart' },
    { name: 'unit', label: 'Unit (optional)', type: 'text', placeholder: 'e.g. $, %, h, m, s' },
  ],
  generateDsl: (config, inputVar) => {
    const chartType = String((config.chartType as string | undefined) || 'bar');
    const x = (config.xColumn as string[])?.[0] || 'x';
    const y = (config.yColumn as string[])?.[0] || 'y';
    const yAgg = config.yAggregation as string;

    let yExpr = y;
    if (yAgg) yExpr = `${yAgg}(${y})`;

    let dsl = `${inputVar}.chart(${chartType}, x: ${x}, y: ${yExpr}`;
    if ((config.colorColumn as string[])?.[0]) dsl += `, color: ${(config.colorColumn as string[])[0]}`;
    if (config.title) dsl += `, title: "${String(config.title as string)}"`;
    if (config.unit) dsl += `, unit: "${String(config.unit as string)}"`;
    dsl += ')';
    return dsl;
  },
};

// ============================================================================
// EXIT NODES
// ============================================================================

export const OUTPUT_NODE: NodeSpec = {
  type: 'output',
  label: 'Output',
  description: 'Write to destination table',
  icon: 'Upload',
  category: 'exit',
  color: '#059669',
  inputs: 1,
  outputs: 0,
  fields: [
    { name: 'table', label: 'Destination Table', type: 'text', required: true, placeholder: 'silver.customers' },
    {
      name: 'mode',
      label: 'Write Mode',
      type: 'select',
      options: [
        { value: 'replace', label: 'Replace' },
        { value: 'append', label: 'Append' },
        { value: 'upsert', label: 'Upsert' },
      ],
      default: 'replace',
    },
    {
      name: 'keys',
      label: 'Upsert Keys',
      type: 'columns',
      showWhen: { field: 'mode', value: 'upsert' },
    },
    { name: 'partitionBy', label: 'Partition By', type: 'columns', required: false },
  ],
  generateDsl: (config, inputVar) => {
    let dsl = `${inputVar}.output("${String(config.table as string)}", mode: "${String((config.mode as string | undefined) || 'replace')}"`;
    if (config.mode === 'upsert' && (config.keys as string[])?.length) {
      dsl += `, keys: [${(config.keys as string[]).join(', ')}]`;
    }
    if ((config.partitionBy as string[])?.length) {
      dsl += `, partition_by: [${(config.partitionBy as string[]).join(', ')}]`;
    }
    dsl += ')';
    return dsl;
  },
};

// ============================================================================
// NODE REGISTRY
// ============================================================================

export const NODE_SPECS: Record<string, NodeSpec> = {
  source: SOURCE_NODE,
  filter: FILTER_NODE,
  select: SELECT_NODE,
  drop: DROP_NODE,
  dedupe: DEDUPE_NODE,
  sort: SORT_NODE,
  limit: LIMIT_NODE,
  rename: RENAME_NODE,
  cast: CAST_NODE,
  add: ADD_NODE,
  with_columns: WITH_COLUMNS_NODE,
  join: JOIN_NODE,
  group_by: GROUP_BY_NODE,
  window: WINDOW_NODE,
  pivot: PIVOT_NODE,
  unpivot: UNPIVOT_NODE,
  validate: VALIDATE_NODE,
  scd2: SCD2_NODE,
  cdc: CDC_NODE,
  chart: CHART_NODE,
  output: OUTPUT_NODE,
};

export function getNodeSpec(type: string): NodeSpec | undefined {
  return NODE_SPECS[type];
}

export function getNodesByCategory(category: 'entry' | 'transform' | 'exit'): NodeSpec[] {
  return Object.values(NODE_SPECS).filter((spec) => spec.category === category);
}

export function getAllNodeSpecs(): NodeSpec[] {
  return Object.values(NODE_SPECS);
}
